import React, { useState, useMemo } from 'react';
import { Paper, Link } from '@material-ui/core';
import { App } from '../../../../models/app';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import { ViewPermissionsModal } from './ViewPermissionsModal';
import { truncateString } from '../../../../utils/helpers';

interface ComponentProps {
  app: App;
}

export const urlLength = 29;

export const getReadWritePermissionsValues = (
  categoryKey: string,
  entries: [string, unknown][],
): { read: boolean; write: boolean } => {
  let readKey = `permission${categoryKey}Read`;
  let writeKey = `permission${categoryKey}Write`;
  let result = {
    read: false,
    write: false,
  };
  let found = {
    read: false,
    write: false,
  };

  for (let entry of entries) {
    let [key, value] = entry;

    if (found.read && found.write) break;

    if (!found.read && key === readKey) {
      found.read = true;
      result.read = value as boolean;
    }

    if (key.startsWith(writeKey)) {
      found.write = true;
      result.write = value as boolean;
    }
  }
  return result;
};

export const requiredReadWritePermissions = (app: App) => {
  const APP_READ_WRITE_PERMISSION_CATEGORIES = [
    { key: 'Merchant', translationKey: 'general.merchant' },
    { key: 'Employees', translationKey: 'general.employees' },
    { key: 'Inventory', translationKey: 'general.inventory' },
    { key: 'Customers', translationKey: 'general.customers' },
    { key: 'CustomersAddress', translationKey: 'general.customers_address' },
    { key: 'CustomersBirthdate', translationKey: 'general.customers_birth_date' },
    { key: 'CustomersBusinessname', translationKey: 'general.customers_business_name' },
    { key: 'CustomersCard', translationKey: 'general.customers_card' },
    { key: 'CustomersEmail', translationKey: 'general.customers_email_address' },
    { key: 'CustomersNote', translationKey: 'general.customers_note' },
    { key: 'CustomersPhone', translationKey: 'general.customers_phone_number' },
    { key: 'CustomersMarketing', translationKey: 'general.customers_marketing' },
    { key: 'Orders', translationKey: 'general.orders' },
    { key: 'Payments', translationKey: 'general.payments' },
  ];
  const entries = Object.entries(app);
  return APP_READ_WRITE_PERMISSION_CATEGORIES.map(category => {
    let permission = { categoryTranslation: category.translationKey, requiredKey: '' };
    let { read, write } = getReadWritePermissionsValues(category.key, entries);
    if (read && write) {
      permission.requiredKey = 'permission_read_and_write';
    } else if (write) {
      permission.requiredKey = 'permission_write';
    } else if (read) {
      permission.requiredKey = 'permission_read';
    }

    return permission;
  }).filter(permission => permission.requiredKey);
};

export const SupportInformation: React.FC<ComponentProps> = ({ app }): JSX.Element => {
  const { t } = useTranslation();
  const { supportPhone, supportEmail, supportUrl, privacyPolicy, eula } = app;
  const [permissionModal, setPermissionModal] = useState(false);
  const readWritePermissions = useMemo(() => requiredReadWritePermissions(app), [app]);
  const showReadWritePermissions = readWritePermissions.length > 0;
  const checkModalState = () => {
    setPermissionModal(true);
  };

const checkForSupportInformation = () => {
	let displaySupportHeader = !!supportPhone || !!supportEmail || !!supportUrl;
	let displayPrivacyEULAPermissionsInformation = !!privacyPolicy || !!eula || !!showReadWritePermissions;
  let displaySupportEmail = !!supportEmail && !supportEmail.includes('@clover.com');
	return {
	  displaySupportSection: displaySupportHeader || displayPrivacyEULAPermissionsInformation,
	  displaySupportHeader,
    displaySupportEmail
	};
  };
  
const supportInformation = checkForSupportInformation();

return (
  <>
    {supportInformation.displaySupportSection && (
      <Paper square className={styles.support}>
        {supportInformation.displaySupportHeader && (
          <div>
            <h4 className={styles.header}>{t("app_details.support")}</h4>
            {supportPhone && (
              <Link target="_blank" href={`tel:${supportPhone}`} className={styles.supportNumber}>
                <p className="captionText" style={{ color: "#333333" }} data-testid="supportPhone">
                  {supportPhone}
                </p>
              </Link>
            )}

            {supportInformation.displaySupportEmail && (
              <Link target="_blank" href={`mailto:${supportEmail}`}>
                <p className="captionText" data-testid="supportEmail">
                  {supportEmail}
                </p>
              </Link>
            )}

            {supportUrl && (
              <Link target="_blank" href={supportUrl}>
                <p className="captionText" data-testid="supportUrl">
                  {truncateString(supportUrl?.replace(/^\/\/|^.*?:\/\//, ""),urlLength)}
                </p>
              </Link>
            )}
          </div>
        )}

        <div className={supportInformation.displaySupportHeader ? styles.terms : ""} data-testid="permission_privacy_eula_information">
          {showReadWritePermissions && (
            <div style={{ marginBottom: "4px" }}>
              <Link onClick={checkModalState}>
                <p className="captionText">
                  {t("app_details.num_permissions_requested")}
                </p>
              </Link>
            </div>
          )}
          {permissionModal && (
            <ViewPermissionsModal
              onClose={() => setPermissionModal(false)}
              readWritePermissions={readWritePermissions}
            />
          )}
          {privacyPolicy && (
            <Link target="_blank" href={privacyPolicy} data-testid="privacyPolicy">
              <p className="captionText">
                {t("app_details.view_privacy_policy")}
              </p>
            </Link>
          )}
          {eula && (
            <Link target="_blank" href={eula} data-testid="eula">
              <p className="captionText">{t("app_details.view_eula")}</p>
            </Link>
          )}
        </div>
      </Paper>
    )}
  </>
);
};
